import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { toast } from 'sonner';
import { apiClient } from '@/lib/axios';
import { useAuthStore } from '@/stores/auth.store';
import type { ApiResponse, UserRole } from '@vayu-saathi/shared-types';

// The login name is the work email or, until IT has created the mailbox, the employee ID
const loginSchema = z.object({
  email: z.string().trim().min(1, 'Enter your work email or employee ID'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

export type LoginInput = z.infer<typeof loginSchema>;

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  employee: {
    id: string;
    organizationId: string;
    orgName: string;
    role: UserRole;
    firstName: string;
    lastName: string;
    workEmail: string | null;
    employeeCode: string;
    avatarUrl: string | null;
    mustChangePassword: boolean;
  };
}

export { loginSchema };

export function useLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { setTokens, setUser } = useAuthStore();

  return useMutation({
    mutationFn: async (input: LoginInput) => {
      const res = await apiClient.post<ApiResponse<LoginResponse>>('/auth/login', input);
      return res.data.data;
    },
    onSuccess: (data) => {
      // Start from an empty cache: nothing from a previous session on this tab may show
      qc.clear();
      setTokens(data.accessToken, data.refreshToken);
      setUser({
        id: data.employee.id,
        organizationId: data.employee.organizationId,
        orgName: data.employee.orgName,
        role: data.employee.role,
        firstName: data.employee.firstName,
        lastName: data.employee.lastName,
        workEmail: data.employee.workEmail,
        employeeCode: data.employee.employeeCode,
        avatarUrl: data.employee.avatarUrl,
        mustChangePassword: data.employee.mustChangePassword,
      });
      toast.success(`Welcome back, ${data.employee.firstName}!`);
      // First login (temporary password): AuthGuard keeps them on /change-password until done
      if (data.employee.mustChangePassword) {
        void navigate('/change-password', { replace: true });
        return;
      }
      // Back to the page AuthGuard sent them here from, if any
      const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
      void navigate(from && from !== '/login' ? from : '/dashboard', { replace: true });
    },
  });
}

export function useLogout() {
  const { logout } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      await apiClient.post('/auth/logout');
    },
    onSettled: () => {
      logout();
      void navigate('/login');
    },
    // The local session is cleared either way — a failed server call isn't worth a toast
    meta: { suppressErrorToast: true },
  });
}

// Same rules as the API's changePasswordSchema (apps/api/src/modules/auth/auth.schema.ts)
export const newPasswordRules = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
    'Password must contain uppercase, lowercase, number and special character (@$!%*?&)',
  );

/**
 * `requireCurrent` is false on the first login: the employee has just signed in with
 * the default password they were given, so only the new one is asked for.
 */
export function changePasswordSchema(requireCurrent: boolean) {
  return z
    .object({
      currentPassword: z.string().optional(),
      newPassword: newPasswordRules,
      confirmPassword: z.string(),
    })
    .superRefine((d, ctx) => {
      if (requireCurrent && (d.currentPassword ?? '').length < 8) {
        ctx.addIssue({
          code: 'custom',
          path: ['currentPassword'],
          message: 'Password must be at least 8 characters',
        });
      }
      if (d.newPassword !== d.confirmPassword) {
        ctx.addIssue({
          code: 'custom',
          path: ['confirmPassword'],
          message: 'Passwords do not match',
        });
      }
      if (requireCurrent && d.currentPassword && d.newPassword === d.currentPassword) {
        ctx.addIssue({
          code: 'custom',
          path: ['newPassword'],
          message: 'New password must be different from your current password',
        });
      }
    });
}

export type ChangePasswordInput = z.infer<ReturnType<typeof changePasswordSchema>>;

export function useChangePassword() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({ currentPassword, ...rest }: ChangePasswordInput) => {
      // First login sends no current password at all
      const body = currentPassword ? { currentPassword, ...rest } : rest;
      await apiClient.post<ApiResponse<{ message: string }>>('/auth/change-password', body);
    },
    onSuccess: () => {
      const { user, setUser } = useAuthStore.getState();
      if (user) setUser({ ...user, mustChangePassword: false });
      toast.success('Password changed successfully');
      void navigate('/dashboard', { replace: true });
    },
    meta: { errorMessage: 'Failed to change password' },
  });
}
